/**
 * AI Reports service layer - configurable fleet reports generated by the
 * AI agent (daily / weekly / monthly / yearly), with per-company section
 * selection. Reports are persisted server-side and stay accessible here
 * after email delivery.
 *
 * All calls go through cdnktapi (authenticated axios instance); company
 * scoping is resolved server-side from the session token.
 */
import axios from "./axios";

export type ReportFrequency = "daily" | "weekly" | "monthly" | "yearly";

export const REPORT_FREQUENCIES: { id: ReportFrequency; label: string }[] = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

export interface ReportSectionMeta {
  key: string;
  label: string;
}

export interface ReportConfig {
  company_id: number;
  /** Legacy single cadence (first of frequencies) - kept for compat. */
  frequency: ReportFrequency;
  /** Multi-cadence: big fleets can enable daily AND weekly AND monthly... */
  frequencies: ReportFrequency[];
  sections: string[];
  recipients: string[];
  enabled: boolean;
  available_sections: ReportSectionMeta[];
}

export interface ReportSummary {
  id: number;
  frequency: ReportFrequency;
  period_label: string;
  title: string;
  emailed_at: string | null;
  created_at: string;
}

export interface Report extends ReportSummary {
  sections: string[];
  content: string;
}

export const getReportConfig = async (): Promise<ReportConfig> => {
  const { data } = await axios.get<ReportConfig>("/ai-agent/reports/config");
  return data;
};

export const saveReportConfig = async (config: {
  frequencies: ReportFrequency[];
  sections: string[];
  recipients: string[];
  enabled: boolean;
}): Promise<ReportConfig> => {
  const { data } = await axios.put<ReportConfig>(
    "/ai-agent/reports/config",
    config
  );
  return data;
};

export const generateReport = async (opts?: {
  frequency?: ReportFrequency;
  sections?: string[];
  send_email?: boolean;
}): Promise<Report> => {
  const { data } = await axios.post<Report>(
    "/ai-agent/reports/generate",
    opts ?? {},
    // Generation aggregates fleet data + an LLM summary pass.
    { timeout: 180000 }
  );
  return data;
};

export const listReports = async (limit = 20): Promise<ReportSummary[]> => {
  const { data } = await axios.get<{ reports: ReportSummary[] }>(
    "/ai-agent/reports",
    { params: { limit } }
  );
  return data.reports ?? [];
};

export const getReport = async (id: number): Promise<Report> => {
  const { data } = await axios.get<Report>(`/ai-agent/reports/${id}`);
  return data;
};

/**
 * Download a report as a branded PDF file.
 * Returns a Blob that can be saved via the browser's download API.
 */
export const downloadReportPdf = async (
  id: number,
  lang = "fr",
): Promise<{ blob: Blob; filename: string }> => {
  const response = await axios.get(`/ai-agent/reports/${id}/pdf`, {
    params: { lang },
    responseType: "blob",
  });

  const contentDisposition =
    response.headers?.["content-disposition"] ?? "";
  let filename = "CodeNekt for Fleet - Rapport.pdf";
  const match = contentDisposition.match(/filename="?([^"]+)"?/);
  if (match) filename = match[1];

  return { blob: response.data as Blob, filename };
};